import { useState, useEffect } from "react"
import type { FormEvent, ChangeEvent } from "react"

import {
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Skeleton,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Switch,
  Typography,
} from '@mui/material'

import { settingsService, type AppSettings, type UpdateAppSettingsRequest } from "../../api/services/settings"

import RequiredFieldsSelector from "./RequiredFieldsSelector"

import type { HealthProfileFieldName } from '@auracare/shared'

const DEFAULT_PLAN_BUTTON_URL = 'https://plan.aura.care/vagus-9v24d'

const MIN_HOLD_HOURS = 1
const MAX_HOLD_HOURS = 168

const parseHoldHours = (value: string): number | null => {
  const hours = Number(value)
  return Number.isInteger(hours) && hours >= MIN_HOLD_HOURS && hours <= MAX_HOLD_HOURS ? hours : null
}

const isValidPlanButtonUrl = (value: string) => {
  try {
    const url = new URL(value)
    return /^https:\/\/plan\.aura\.care(?:[/?#]|$)/i.test(value) &&
      url.protocol === 'https:' && url.hostname === 'plan.aura.care' && !/\s/.test(value)
  } catch {
    return false
  }
}

const isValidHttpsUrl = (value: string) => {
  try {
    return new URL(value).protocol === 'https:' && !/\s/.test(value)
  } catch {
    return false
  }
}

interface SettingsFormProps {
  onSave?: (settings: AppSettings) => void
  onError?: (error: string) => void
}

export default function SettingsForm({ onSave, onError }: SettingsFormProps) {
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  // False when the server response predates TEC-554 (deploy skew/rollback):
  // the switch is disabled then, because the old server's whitelist validation
  // would reject any save containing labsTabEnabled.
  const [serverHasLabsFlag, setServerHasLabsFlag] = useState(true)
  const [serverHasPlanUrl, setServerHasPlanUrl] = useState(true)
  const [serverHasExperienceCallFlag, setServerHasExperienceCallFlag] = useState(false)
  const [serverHasLoginLinksFlag, setServerHasLoginLinksFlag] = useState(false)
  const [serverHasPurchaseReceiptFlags, setServerHasPurchaseReceiptFlags] = useState(false)
  const [serverHasFfbillingReceiptFlag, setServerHasFfbillingReceiptFlag] = useState(false)
  const [serverHasReceiptCancelUrl, setServerHasReceiptCancelUrl] = useState(false)
  const [formData, setFormData] = useState({
    model: "",
    maxNumberOfOnboardingQuestions: 20,
    minNumberOfOnboardingQuestions: 15,
    requiredProfileFields: [] as HealthProfileFieldName[],
    minProfileCompletionPercentage: 30,
    forwardReportsToEmails: "",
    labsTabEnabled: true,
    planButtonUrl: DEFAULT_PLAN_BUTTON_URL,
    experienceCallEnabled: false,
    universalLoginLinksEnabled: false,
    funnelfoxStripeEnabled: false,
    funnelfoxPaypalEnabled: false,
    funnelfoxFfbillingEnabled: false,
    missingNextPolicy: "hold",
    missingNextHoldHours: "24",
    missingNextAfterHold: "send_partial",
    purchaseReceiptCancelUrl: "",
  })

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true)
        const data = await settingsService.getAppSettings()
        setServerHasLabsFlag(data.labsTabEnabled !== undefined)
        setServerHasPlanUrl(data.planButtonUrl !== undefined)
        setServerHasExperienceCallFlag(typeof data.experienceCallEnabled === "boolean")
        setServerHasLoginLinksFlag(typeof data.universalLoginLinksEnabled === "boolean")
        setServerHasPurchaseReceiptFlags(typeof data.funnelfoxStripeEnabled === "boolean")
        setServerHasFfbillingReceiptFlag(typeof data.funnelfoxFfbillingEnabled === "boolean")
        setServerHasReceiptCancelUrl(data.purchaseReceiptCancelUrl !== undefined)
        // Single normalization point for deploy skew: an older server omits
        // the newer fields, which means the pre-flag behavior for each.
        const normalized = {
          ...data,
          experienceCallEnabled: data.experienceCallEnabled === true,
          universalLoginLinksEnabled: data.universalLoginLinksEnabled === true,
          labsTabEnabled: data.labsTabEnabled ?? true,
          planButtonUrl: data.planButtonUrl ?? DEFAULT_PLAN_BUTTON_URL,
          funnelfoxStripeEnabled: data.funnelfoxStripeEnabled === true,
          funnelfoxPaypalEnabled: data.funnelfoxPaypalEnabled === true,
          funnelfoxFfbillingEnabled: data.funnelfoxFfbillingEnabled === true,
          funnelStripeEnabled: data.funnelStripeEnabled === true,
          funnelPaypalEnabled: data.funnelPaypalEnabled === true,
          missingNextPolicy: data.missingNextPolicy ?? "hold",
          missingNextHoldHours: data.missingNextHoldHours ?? 24,
          missingNextAfterHold: data.missingNextAfterHold ?? "send_partial",
          purchaseReceiptCancelUrl: data.purchaseReceiptCancelUrl ?? "",
        }
        setSettings(normalized)
        setFormData({
          model: normalized.model,
          maxNumberOfOnboardingQuestions: normalized.maxNumberOfOnboardingQuestions,
          minNumberOfOnboardingQuestions: normalized.minNumberOfOnboardingQuestions,
          requiredProfileFields: normalized.requiredProfileFields || [],
          minProfileCompletionPercentage: normalized.minProfileCompletionPercentage,
          forwardReportsToEmails: normalized.forwardReportsToEmails || "",
          labsTabEnabled: normalized.labsTabEnabled,
          planButtonUrl: normalized.planButtonUrl,
          experienceCallEnabled: normalized.experienceCallEnabled,
          universalLoginLinksEnabled: normalized.universalLoginLinksEnabled,
          funnelfoxStripeEnabled: normalized.funnelfoxStripeEnabled,
          funnelfoxPaypalEnabled: normalized.funnelfoxPaypalEnabled,
          funnelfoxFfbillingEnabled: normalized.funnelfoxFfbillingEnabled,
          missingNextPolicy: normalized.missingNextPolicy,
          missingNextHoldHours: String(normalized.missingNextHoldHours),
          missingNextAfterHold: normalized.missingNextAfterHold,
          purchaseReceiptCancelUrl: normalized.purchaseReceiptCancelUrl,
        })
      } catch (err) {
        onError?.(err instanceof Error ? err.message : "Failed to load settings")
      } finally {
        setIsLoading(false)
      }
    }

    fetchSettings()
  }, [onError])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!settings) return

    try {
      setIsSaving(true)

      const updateData: UpdateAppSettingsRequest = {}

      if (formData.model !== settings.model) {
        updateData.model = formData.model
      }
      if (formData.maxNumberOfOnboardingQuestions !== settings.maxNumberOfOnboardingQuestions) {
        updateData.maxNumberOfOnboardingQuestions = formData.maxNumberOfOnboardingQuestions
      }
      if (formData.minNumberOfOnboardingQuestions !== settings.minNumberOfOnboardingQuestions) {
        updateData.minNumberOfOnboardingQuestions = formData.minNumberOfOnboardingQuestions
      }
      if (JSON.stringify(formData.requiredProfileFields) !== JSON.stringify(settings.requiredProfileFields)) {
        updateData.requiredProfileFields = formData.requiredProfileFields
      }
      if (formData.minProfileCompletionPercentage !== settings.minProfileCompletionPercentage) {
        updateData.minProfileCompletionPercentage = formData.minProfileCompletionPercentage
      }
      if (formData.forwardReportsToEmails !== (settings.forwardReportsToEmails || "")) {
        updateData.forwardReportsToEmails = formData.forwardReportsToEmails
      }
      if (serverHasLabsFlag && formData.labsTabEnabled !== settings.labsTabEnabled) {
        updateData.labsTabEnabled = formData.labsTabEnabled
      }
      if (serverHasPlanUrl && formData.planButtonUrl !== settings.planButtonUrl) {
        if (!isValidPlanButtonUrl(formData.planButtonUrl)) {
          onError?.('The plan button URL must be an https URL on plan.aura.care')
          return
        }
        updateData.planButtonUrl = formData.planButtonUrl
      }

      if (serverHasExperienceCallFlag && formData.experienceCallEnabled !== settings.experienceCallEnabled) {
        updateData.experienceCallEnabled = formData.experienceCallEnabled
      }

      if (serverHasLoginLinksFlag && formData.universalLoginLinksEnabled !== settings.universalLoginLinksEnabled) {
        updateData.universalLoginLinksEnabled = formData.universalLoginLinksEnabled
      }

      if (serverHasPurchaseReceiptFlags) {
        if (formData.funnelfoxStripeEnabled !== settings.funnelfoxStripeEnabled) {
          updateData.funnelfoxStripeEnabled = formData.funnelfoxStripeEnabled
        }
        if (formData.funnelfoxPaypalEnabled !== settings.funnelfoxPaypalEnabled) {
          updateData.funnelfoxPaypalEnabled = formData.funnelfoxPaypalEnabled
        }
        if (
          serverHasFfbillingReceiptFlag &&
          formData.funnelfoxFfbillingEnabled !== settings.funnelfoxFfbillingEnabled
        ) {
          updateData.funnelfoxFfbillingEnabled = formData.funnelfoxFfbillingEnabled
        }
        if (formData.missingNextPolicy !== settings.missingNextPolicy) {
          updateData.missingNextPolicy = formData.missingNextPolicy
        }
        const holdHours = parseHoldHours(formData.missingNextHoldHours)
        if (holdHours === null) {
          onError?.(`Missing next-charge hold hours must be a whole number from ${MIN_HOLD_HOURS} to ${MAX_HOLD_HOURS}`)
          return
        }
        if (holdHours !== settings.missingNextHoldHours) {
          updateData.missingNextHoldHours = holdHours
        }
        if (formData.missingNextAfterHold !== settings.missingNextAfterHold) {
          updateData.missingNextAfterHold = formData.missingNextAfterHold
        }
      }

      if (serverHasReceiptCancelUrl) {
        const cancelUrl = formData.purchaseReceiptCancelUrl.trim()
        if (cancelUrl && cancelUrl !== (settings.purchaseReceiptCancelUrl ?? "")) {
          if (!isValidHttpsUrl(cancelUrl)) {
            onError?.('The purchase receipt cancel URL must be a valid https URL')
            return
          }
          updateData.purchaseReceiptCancelUrl = cancelUrl
        }
      }

      if (Object.keys(updateData).length === 0) {
        return // No changes
      }

      const updatedSettings = await settingsService.updateAppSettings(updateData)
      setSettings(updatedSettings)
      onSave?.(updatedSettings)
    } catch (err) {
      onError?.(err instanceof Error ? err.message : "Failed to save settings")
    } finally {
      setIsSaving(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData) => (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'model' ? e.target.value : parseInt(e.target.value) || 0
    setFormData((prev: typeof formData) => ({ ...prev, [field]: value }))
  }

  const handleRequiredFieldsChange = (fields: HealthProfileFieldName[]) => {
    setFormData((prev) => ({ ...prev, requiredProfileFields: fields }))
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Skeleton variant="text" width={200} height={24} />
            <Skeleton variant="rectangular" width="100%" height={56} />
            <Skeleton variant="text" width={300} height={24} />
            <Skeleton variant="rectangular" width="100%" height={56} />
            <Skeleton variant="text" width={300} height={24} />
            <Skeleton variant="rectangular" width="100%" height={56} />
            <Skeleton variant="rectangular" width={120} height={36} />
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent sx={{ p: 4 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="AI Model"
            value={formData.model}
            onChange={handleInputChange('model')}
            placeholder="e.g., gpt-5"
            required
            helperText="The AI model to use for generating responses"
          />

          <TextField
            fullWidth
            label="Maximum Number of Onboarding Questions"
            type="number"
            inputProps={{ min: 1, max: 50 }}
            value={formData.maxNumberOfOnboardingQuestions}
            onChange={handleInputChange('maxNumberOfOnboardingQuestions')}
            required
            helperText="Maximum number of questions that get generated and delivered to mobile client for follow-up section of the onboarding, usually we ask this before generating the health report"
          />

          <TextField
            fullWidth
            label="Minimum Number of Onboarding Questions"
            type="number"
            inputProps={{ min: 1, max: 50 }}
            value={formData.minNumberOfOnboardingQuestions}
            onChange={handleInputChange('minNumberOfOnboardingQuestions')}
            required
            helperText="Minimum number of questions to ask during onboarding, see max number of questions explanation above"
          />

          <RequiredFieldsSelector
            value={formData.requiredProfileFields}
            onChange={handleRequiredFieldsChange}
          />

          <TextField
            fullWidth
            label="Minimum Profile Completion Percentage"
            type="number"
            slotProps={{
              htmlInput: { min: 0, max: 100 }
            }}
            value={formData.minProfileCompletionPercentage}
            onChange={handleInputChange('minProfileCompletionPercentage')}
            required
            helperText="Percentage of profile completion required for health report generation. Based on filled fields and answered questions (0-100%)."
          />

          <TextField
            fullWidth
            label="Forward Reports To Emails"
            value={formData.forwardReportsToEmails}
            onChange={(e) => setFormData(prev => ({ ...prev, forwardReportsToEmails: e.target.value }))}
            placeholder="e.g., lab-reports@aura.care,admin@aura.care"
            helperText="Comma-separated list of emails to forward lab reports to. Leave empty to disable forwarding."
          />

          <TextField
            fullWidth
            label="'Get the plan' button URL (Android app)"
            value={formData.planButtonUrl}
            disabled={!serverHasPlanUrl}
            error={serverHasPlanUrl && !isValidPlanButtonUrl(formData.planButtonUrl)}
            onChange={(e) => setFormData(prev => ({ ...prev, planButtonUrl: e.target.value }))}
            placeholder={DEFAULT_PLAN_BUTTON_URL}
            helperText={serverHasPlanUrl
              ? "Target of the login screen's 'Get the plan' button in the Android app (TEC-692). Must be an https URL on plan.aura.care; the app ignores anything else. Users pick the change up on their next app launch."
              : 'The connected server does not support this field yet (older deployment) — it is disabled until the server catches up.'}
          />

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.labsTabEnabled}
                  disabled={!serverHasLabsFlag}
                  onChange={(e) => setFormData(prev => ({ ...prev, labsTabEnabled: e.target.checked }))}
                />
              }
              label="Labs tab visible in the mobile app"
            />
            <FormHelperText>
              {serverHasLabsFlag
                ? 'Served to the app through the public /app-config endpoint. Turn off to hide the Labs tab for all users without an app release (labs sunset, TEC-554). Users pick the change up on their next app launch.'
                : 'The connected server does not support this flag yet (older deployment) — the toggle is disabled until the server catches up.'}
            </FormHelperText>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.experienceCallEnabled}
                  disabled={!serverHasExperienceCallFlag}
                  onChange={(e) => setFormData(prev => ({ ...prev, experienceCallEnabled: e.target.checked }))}
                />
              }
              label="Interview invitation in app 1.0.48"
            />
            <FormHelperText>
              {serverHasExperienceCallFlag
                ? 'Controls the existing invitation in app 1.0.48. Independent of the audience settings for 1.0.49 and later. Changes apply on the next app launch.'
                : 'This server does not support the interview invitation switch yet.'}
            </FormHelperText>
            <Typography variant="body2">
              Interview audience and booking settings for 1.0.49 and later are managed on the{' '}
              <a href="/custdev">Custdev interviews page</a>.
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.universalLoginLinksEnabled}
                  disabled={!serverHasLoginLinksFlag}
                  onChange={(e) => setFormData(prev => ({ ...prev, universalLoginLinksEnabled: e.target.checked }))}
                />
              }
              label="Open Aura from login emails"
            />
            <FormHelperText>
              {serverHasLoginLinksFlag
                ? 'Add a link that opens Aura to login emails for supported app versions. Older apps keep their existing emails. The login code remains available.'
                : 'This server does not support app login email links yet.'}
            </FormHelperText>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.funnelfoxStripeEnabled}
                  disabled={!serverHasPurchaseReceiptFlags}
                  onChange={(e) => setFormData(prev => ({ ...prev, funnelfoxStripeEnabled: e.target.checked }))}
                />
              }
              label="FunnelFox × Stripe purchase receipts"
            />
            <FormHelperText>
              {serverHasPurchaseReceiptFlags
                ? 'Queue first-purchase receipt emails for FunnelFox Stripe subscriptions (sub_…). Default off.'
                : 'This server does not support purchase-receipt flags yet.'}
            </FormHelperText>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.funnelfoxPaypalEnabled}
                  disabled={!serverHasPurchaseReceiptFlags}
                  onChange={(e) => setFormData(prev => ({ ...prev, funnelfoxPaypalEnabled: e.target.checked }))}
                />
              }
              label="FunnelFox × PayPal purchase receipts"
            />
            <FormHelperText>
              {serverHasPurchaseReceiptFlags
                ? 'Queue first-purchase receipt emails for FunnelFox PayPal subscriptions (I-…). Default off.'
                : 'This server does not support purchase-receipt flags yet.'}
            </FormHelperText>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.funnelfoxFfbillingEnabled}
                  disabled={!serverHasFfbillingReceiptFlag}
                  onChange={(e) => setFormData(prev => ({ ...prev, funnelfoxFfbillingEnabled: e.target.checked }))}
                />
              }
              label="FunnelFox × FF Billing purchase receipts"
            />
            <FormHelperText>
              {serverHasFfbillingReceiptFlag
                ? 'Queue first-purchase receipt emails for FunnelFox FF Billing subscriptions, including Adyen. One receipt per FF subscription. Default off.'
                : 'This server does not support FF Billing purchase receipts yet.'}
            </FormHelperText>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Purchase receipt: when the next charge is unknown
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The receipt tells the customer the date and amount of their next charge — the first renewal after the
              trial or the first billing period. Stripe usually knows it right away; PayPal often reports it minutes
              or hours after the purchase. These settings decide what happens to a receipt while that date is still
              unknown.
            </Typography>

            <TextField
              select
              fullWidth
              label="While the next charge is unknown"
              disabled={!serverHasPurchaseReceiptFlags}
              value={formData.missingNextPolicy}
              onChange={(e) => setFormData(prev => ({ ...prev, missingNextPolicy: e.target.value }))}
              helperText="Waiting keeps the receipt complete. Sending now reaches the customer sooner, but without the next charge date and amount."
              SelectProps={{ native: true }}
            >
              <option value="hold">Wait for it before sending</option>
              <option value="send_partial">Send now without the next charge</option>
            </TextField>

            <TextField
              fullWidth
              label="Maximum wait, hours"
              type="number"
              disabled={!serverHasPurchaseReceiptFlags || formData.missingNextPolicy !== "hold"}
              inputProps={{ min: MIN_HOLD_HOURS, max: MAX_HOLD_HOURS, step: 1 }}
              value={formData.missingNextHoldHours}
              onChange={(e) => setFormData(prev => ({ ...prev, missingNextHoldHours: e.target.value }))}
              helperText={`How long a receipt waits for the next charge date (${MIN_HOLD_HOURS}–${MAX_HOLD_HOURS}). It is checked again every 5 minutes. Used only when waiting.`}
            />

            <TextField
              select
              fullWidth
              label="If it is still unknown after the wait"
              disabled={!serverHasPurchaseReceiptFlags || formData.missingNextPolicy !== "hold"}
              value={formData.missingNextAfterHold}
              onChange={(e) => setFormData(prev => ({ ...prev, missingNextAfterHold: e.target.value }))}
              helperText="Either way ops gets one alert when the wait runs out. Used only when waiting."
              SelectProps={{ native: true }}
            >
              <option value="send_partial">Send without the next charge</option>
              <option value="fail">Do not send (mark as failed)</option>
            </TextField>
          </Box>

          <TextField
            fullWidth
            label="Purchase receipt cancel URL"
            disabled={!serverHasReceiptCancelUrl}
            value={formData.purchaseReceiptCancelUrl}
            error={
              serverHasReceiptCancelUrl &&
              formData.purchaseReceiptCancelUrl.trim() !== "" &&
              !isValidHttpsUrl(formData.purchaseReceiptCancelUrl.trim())
            }
            onChange={(e) => setFormData(prev => ({ ...prev, purchaseReceiptCancelUrl: e.target.value }))}
            helperText={
              serverHasReceiptCancelUrl
                ? 'Cancel-subscription link shown in the receipt email. Enter it without the email: the recipient email is added to it automatically. Cannot be cleared once set from here.'
                : 'This server does not support the receipt cancel URL yet.'
            }
          />

          <Button
            type="submit"
            variant="contained"
            disabled={isSaving}
            sx={{ alignSelf: 'flex-start' }}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}
